<template>
  <general-layout v-if="canPerform('authcore.users.list')">
    <template v-slot:alert v-if="authenticated">
      <b-alert
        variant="success"
        :show="localShowCreateUserAlert"
        dismissible
        @dismissed="localShowCreateUserAlert = false"
        class="web-alert-absolute web-alert-style text-center rounded-0"
      >
        <span class="align-middle">{{ $t('user_list.text.create_user_successfully') }}</span>
      </b-alert>
      <b-alert
        variant="danger"
        show
        v-if="error !== undefined"
      >
        <i18n path="status.error.general" tag="div">
          <span place="message">{{ error.message }}</span>
        </i18n>
      </b-alert>
    </template>
    <loading-spinner v-if="!authenticated" />
    <div v-else>
      <b-bsq-modal
        ref="modal"
        v-model="isModalEnabled"
        :header-title="modalHeaderTitle"
        :button-title="modalButtonTitle"
        :button-variant="modalButtonVariant"
        @ok="modalSubmit"
      >
        <div class="mt-3 mb-5">
          <div v-if="isAssignRoleModalEnabled">
            <i18n path="description.new_role_assign" tag="div" class="mb-3">
              <span class="font-weight-bold" place="username">{{ user.displayName }}</span>
            </i18n>
            <!-- TODO: Change to use b-bsq-input when it implements the multiselect feature -->
            <!-- https://gitlab.com/blocksq/bootstrap-vue/issues/28 -->
            <multiselect :value="selectedRoles" track="name" label="name" :options="roles" :multiple="true" @select="select" @remove="remove">
              <!-- <span>{{ option.name }}</span> -->
            </multiselect>
          </div>
          <div v-else-if="isLockUserModalEnabled">
            <div v-if="userLocked">
              <i18n path="description.unlock_user" tag="div" class="mb-3">
                <span class="font-weight-bold" place="username">{{ user.displayName }}</span>
              </i18n>
            </div>
            <div v-else>
              <i18n path="description.lock_user" tag="div" class="mb-3">
                <span class="font-weight-bold" place="username">{{ user.displayName }}</span>
              </i18n>
              <b-bsq-input
                required
                readonly
                v-model="lockContent"
                :label="$t('actions.lock_user')"
              />
            </div>
          </div>
          <div v-else-if="isChangePasswordModalEnabled">
            <i18n path="description.change_password" tag="div" class="mb-3">
              <span class="font-weight-bold" place="username">{{ user.displayName }}</span>
            </i18n>
            <b-bsq-input
              v-model="newPassword"
              :disabled="actionLoading"
              :label="$t('input.password')"
              type="password"
              autocomplete="new-password"
              class="my-3" />
            <b-bsq-input
              v-model="confirmNewPassword"
              :disabled="actionLoading"
              :label="$t('input.confirm_password')"
              type="password"
              autocomplete="new-password"
              class="my-3" />
          </div>
        </div>
      </b-bsq-modal>

      <b-container class="mt-5">
        <b-row>
          <b-col>
            <h2>{{ $t('title.users') }}</h2>
          </b-col>
        </b-row>
        <b-row class="my-3">
          <b-col class="text-right">
            <b-button
              v-if="canPerform('authcore.users.create')"
              variant="primary"
              size="sm"
              @click="showCreateUserView">
              {{ $t('actions.create_user') }}
            </b-button>
          </b-col>
        </b-row>
        <div id="filters" class="d-flex mb-5">
          <!-- TODO: search box corresponds to https://gitlab.com/blocksq/authcore/issues/223 -->
          <div id="search" class="border-theme-general d-inline-flex mr-3 w-75">
            <b-button-group class="w-25">
              <b-dropdown class="w-100" toggle-class="d-inline-flex text-truncate text-decoration-none py-3 font:0.875rem text-grey text-hover-grey" variant="link" no-caret>
                <template v-slot:button-content>
                  <span class="text-left flex-grow-1">{{ $t(queryKey.name) }}</span>
                  <div>
                    <i class="ml-2 fas fa-chevron-down"></i>
                  </div>
                </template>
                <b-dropdown-item v-for="(item, index) in queryKeys" :key="index" v-on:click="changeQuery(item)" :class="{ 'background-grey': queryKey === item }">
                  <span class="font:0.875rem" :class="{ 'text-primary': queryKey === item }">{{ $t(item.name) }}</span>
                </b-dropdown-item>
              </b-dropdown>
            </b-button-group>
            <div class="vertical-stroke" style="margin-left: 0.2rem; border-right: 1px solid rgb(223, 223, 223);"></div>
            <b-form class="d-inline-flex w-75" @submit="submitQuery">
              <b-form-input
                v-model="queryValue"
                class="h-100 font:0.875rem py-0 border-0 border-hover-0 border-focus-0"
                type="text"
                :placeholder='queryPlaceholder'
              />
              <b-button type="submit" variant="link" class="text-grey text-hover-grey">
                <i class="fas fa-search"></i>
              </b-button>
            </b-form>
          </div>
          <div id="sort" class="border-theme-general w-25">
            <b-button-group class="w-100">
              <b-button-group class="w-75">
                <b-dropdown class="w-100" toggle-class="d-inline-flex text-truncate text-decoration-none py-3 font:0.875rem text-grey text-hover-grey" variant="link" no-caret>
                  <template v-slot:button-content>
                    <span class="text-left flex-grow-1">{{ $t(sortKey.name) }}</span>
                    <div>
                      <i class="ml-2 fas fa-chevron-down"></i>
                    </div>
                  </template>
                  <b-dropdown-item v-for="(item, index) in sortKeys" :key="index" v-on:click="changeSort(item)" :class="{ 'background-grey': sortKey === item }">
                    <span class="font:0.875rem" :class="{ 'text-primary': sortKey === item }">{{ $t(item.name) }}</span>
                  </b-dropdown-item>
                </b-dropdown>
              </b-button-group>
              <div class="vertical-stroke" style="margin-left: 0.2rem; border-right: 1px solid rgb(223, 223, 223);"></div>
              <b-button class="w-25 p-0" variant="link" @click="changeOrder">
                <img alt="" src="@/assets/sort-icon.png" :class="{ 'rotateX-180': ascending }" />
              </b-button>
            </b-button-group>
          </div>
        </div>
        <b-table
          show-empty
          class="theme-full item-clickable"
          cellspacing="0"
          :items="users"
          :fields="fields"
          @row-clicked="viewItem"
        >
          <template v-slot:cell()="data">
            <div v-if="loading" class="ph-item">
              <div class="ph-custom-avatar"></div>
            </div>
            <div v-else>
              <div class="list-monogram" :data-letter="data.item.profileName[0].toUpperCase()"></div>
            </div>
          </template>
          <template v-slot:cell(username)="data">
            <with-loading-content :loading="loading">
              {{ data.value }}
            </with-loading-content>
          </template>
          <template v-slot:cell(primaryEmail)="data">
            <with-loading-content :loading="loading">
              {{ data.value }}
            </with-loading-content>
          </template>
          <template v-slot:cell(primaryPhone)="data">
            <with-loading-content :loading="loading">
              {{ data.value }}
            </with-loading-content>
          </template>
          <template v-slot:cell(displayName)="data">
            <with-loading-content :loading="loading">
              {{ data.value }}
            </with-loading-content>
          </template>
          <!-- Remove eslint disable when row is used in actions -->
          <!-- eslint-disable -->
          <template v-slot:cell(actions)="data" class="text-center">
            <!-- eslint-enable -->
            <with-loading-content :loading="loading">
              <b-dropdown right no-caret variant="link" toggle-class="p-0">
                <template #button-content>
                  <i aria-hidden="true" class="fas fa-ellipsis-h" />
                </template>
                <b-dropdown-item-button @click="triggerAssignRoleAction(data)">
                  <span class="text-muted">{{ $t('actions.assign_role') }}</span>
                </b-dropdown-item-button>
                <b-dropdown-item-button @click="triggerChangePasswordAction(data)">
                  <span class="text-muted">{{ $t('actions.change_password') }}</span>
                </b-dropdown-item-button>
                <b-dropdown-item-button @click="triggerLockUserAction(data)">
                  <div v-if="data.item.locked" class="text-muted">
                    {{ $t('actions.unlock_user') }}
                  </div>
                  <div v-else class="text-muted">
                    {{ $t('actions.lock_user') }}
                  </div>
                </b-dropdown-item-button>
              </b-dropdown>
            </with-loading-content>
          </template>
        </b-table>
        <b-row align-v="center">
          <b-col cols="6">
            <span class="list-with-table-padding-left">
              {{ $tc('description.total_users', totalItems) }}
            </span>
          </b-col>
          <b-col cols="6" class="text-right">
            <pagination
              class="list-with-table-padding-right"
              button-class="btn-custom"
              :previous-page-token="prevPageToken"
              :next-page-token="nextPageToken"
              @previous-click="previousPage"
              @next-click="nextPage"
            />
          </b-col>
        </b-row>
      </b-container>
    </div>
  </general-layout>
  <b-alert v-else variant="danger" show>
    <i18n path="status.error.not_authorized" tag="div">
      <span place="page">{{ $t('general.user_list') }}</span>
    </i18n>
  </b-alert>
</template>

<style src="vue-multiselect/dist/vue-multiselect.min.css">
</style>

<script>
import { mapState } from 'vuex'
import Multiselect from 'vue-multiselect'

import router from '@/router'
import store from '@/store'

import GeneralLayout from '@/components/GeneralLayout.vue'
import WithLoadingContent from '@/components/WithLoadingContent.vue'
import Pagination from '@/components/Pagination.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'

import i18n from '@/i18n'

export default {
  name: 'UserList',
  components: {
    GeneralLayout,
    WithLoadingContent,
    Pagination,
    LoadingSpinner,

    Multiselect
  },

  props: {
    pageToken: {
      type: String,
      default: ''
    },
    sort: {
      type: String,
      default: ''
    },
    qsQueryKey: {
      type: String,
      default: ''
    },
    qsQueryValue: {
      type: String,
      default: ''
    },
    isShowCreateUserAlert: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      isAssignRoleModalEnabled: false,
      isChangePasswordModalEnabled: false,
      isLockUserModalEnabled: false,

      selectedUserId: -1,

      // Roles assigned for the user
      selectedRoles: [],
      // Roles to be unassigned from the user
      removedRoles: [],
      lockContent: i18n.t('general.permanently'),

      // Change password
      newPassword: '',
      confirmNewPassword: '',

      localShowCreateUserAlert: false,

      fields: [
        { key: 'icon', label: '' },
        { key: 'username', label: 'Username' },
        { key: 'primaryEmail', label: 'E-mail address' },
        { key: 'primaryPhone', label: 'Phone number' },
        { key: 'displayName', label: 'Display name' },
        { key: 'actions', label: 'Actions', thClass: 'text-center' }
      ],
      // Page states
      isLoading: true,
      queryPlaceholder: i18n.t('user_list.input.label.search_for_user'),
      queryKeys: [
        { name: 'user_list.text.all', key: 'all' },
        { name: 'user_list.text.username', key: 'username' },
        { name: 'user_list.text.email', key: 'email' },
        { name: 'user_list.text.phone', key: 'phone' },
        { name: 'user_list.text.display_name', key: 'display_name' }
      ],
      queryKey: '',
      queryValue: '',
      sortKeys: [
        { name: 'user_list.text.lock_user', key: 'is_locked' },
        { name: 'user_list.text.created_date', key: 'created_at' },
        { name: 'user_list.text.last_login_date', key: 'last_seen_at' },
        { name: 'user_list.text.email', key: 'primary_email' }
      ],
      sortKey: '',
      ascending: false,

      rowsPerPage: 10,

      // Constants
      userHeaders: [{
        text: '#',
        value: 'id'
      }, {
        text: 'Username',
        value: 'username'
      }, {
        text: 'E-mail Address',
        sortable: false,
        value: 'email'
      }, {
        text: 'Phone Number',
        sortable: false,
        value: 'phone'
      }, {
        text: 'Display Name',
        value: 'display_name'
      }]
    }
  },

  computed: {
    ...mapState('client', [
      'authenticated'
    ]),
    ...mapState('management/usersList', [
      'users',
      'prevPageToken',
      'nextPageToken',
      'totalItems',

      'loading',
      'error'
    ]),
    ...mapState('management/userDetails', [
      'user',
      'roleAssignments',

      'actionLoading',
      'actionError'
    ]),
    ...mapState('management/rolesList', [
      'roles'
    ]),
    isModalEnabled: {
      get () {
        return this.isAssignRoleModalEnabled ||
               this.isChangePasswordModalEnabled ||
               this.isDisableMFAModalEnabled ||
               this.isLockUserModalEnabled ||
               this.isDeleteUserModalEnabled
      },
      // isModalEnabled will only be set when modal is closed, therefore it sets all modal related
      // value to false. Case for opening modal is handled by individual modal value.
      //
      // Setting timeout as to keep the content in modal when it is fading out.
      set () {
        setTimeout(() => {
          this.isAssignRoleModalEnabled = false
          this.isChangePasswordModalEnabled = false
          this.isLockUserModalEnabled = false
        }, 500)
      }
    },
    modalHeaderTitle () {
      if (this.isAssignRoleModalEnabled) {
        return i18n.t('actions.assign_role')
      } else if (this.isLockUserModalEnabled) {
        if (this.userLocked) {
          return i18n.t('actions.unlock_user')
        } else {
          return i18n.t('actions.lock_user')
        }
      } else if (this.isChangePasswordModalEnabled) {
        return i18n.t('actions.change_password')
      }
      return ''
    },
    modalButtonTitle () {
      if (this.isAssignRoleModalEnabled) {
        return i18n.t('actions.assign_role')
      } else if (this.isLockUserModalEnabled) {
        if (this.userLocked) {
          return i18n.t('actions.unlock_user')
        } else {
          return i18n.t('actions.lock_user')
        }
      } else if (this.isChangePasswordModalEnabled) {
        return i18n.t('actions.change_password')
      }
      return ''
    },
    modalButtonVariant () {
      if (this.isAssignRoleModalEnabled || this.isLockUserModalEnabled || this.isChangePasswordModalEnabled) {
        return 'primary'
      }
      return ''
    },
    userLocked () {
      return (this.user && this.user.locked)
    }
  },

  watch: {
    async authenticated (newVal) {
      if (newVal) {
        // Clear the error message from unauthenticated state
        store.commit('management/usersList/CLEAR_STATES')
        await Promise.all([
          store.dispatch('management/usersList/list', {
            pageToken: this.pageToken,
            ascending: this.ascending,
            sortKey: this.sortKey.key,
            queryKey: this.queryKey.key,
            queryValue: this.queryValue
          }),
          store.dispatch('management/rolesList/list')
        ])
      }
    },

    roleAssignments (newVal) {
      this.selectedRoles = []
      this.removedRoles = []
      // As the object from two API are different by reference, query the item by id and
      // push the item into the selectedRoles array since it requires reference match with
      // roles object, which supplies the multiselect items.
      newVal.forEach(assignmentItem => {
        const roleItem = this.roles.find(item => item.id === assignmentItem.id)
        if (roleItem !== undefined) {
          roleItem.originalAssigned = true
          this.selectedRoles.push(roleItem)
        }
      })
    },

    async user (newVal) {
      await store.dispatch('management/userDetails/listRole', this.user.id)
    },

    sortKey (newVal, oldVal) {
      if (oldVal !== '') {
        router.replace({
          name: 'UserList',
          query: {
            pageToken: '',
            sort: newVal.key,
            ascending: this.ascending,
            queryKey: this.queryKey.key,
            queryValue: this.queryValue
          }
        })
      }
    },

    ascending (newVal) {
      router.replace({
        name: 'UserList',
        query: {
          pageToken: '',
          sort: this.sortKey.key,
          ascending: newVal,
          queryKey: this.queryKey.key,
          queryValue: this.queryValue
        }
      })
    }
  },

  created () {
    // Update this.queryKey according to the query
    if (this.qsQueryKey !== '') {
      this.queryKey = this.queryKeys.find(item => item.key === this.qsQueryKey)
    } else {
      // Default to be the first one 'All'
      this.queryKey = this.queryKeys[0]
    }
    this.queryValue = this.qsQueryValue
    // Update this.sortKey according to the query
    if (this.sort !== '') {
      this.sortKey = this.sortKeys.find(item => item.key === this.sort)
    } else {
      this.sortKey = this.sortKeys.find(item => item.key === 'last_seen_at')
    }
  },
  mounted () {
    this.localShowCreateUserAlert = this.isShowCreateUserAlert
    store.dispatch('management/usersList/list', {
      pageToken: this.pageToken,
      ascending: this.ascending,
      sortKey: this.sortKey.key,
      queryKey: this.queryKey.key,
      queryValue: this.queryValue
    })
    store.dispatch('management/rolesList/list')
  },
  updated () {},
  destroyed () {
    store.commit('management/usersList/CLEAR_STATES')
  },

  methods: {
    previousPage () {
      router.push({
        name: 'UserList',
        query: {
          pageToken: this.prevPageToken,
          sort: this.sortKey.key,
          ascending: this.ascending,
          queryKey: this.queryKey.key,
          queryValue: this.queryValue
        }
      })
    },
    nextPage () {
      router.push({
        name: 'UserList',
        query: {
          pageToken: this.nextPageToken,
          sort: this.sortKey.key,
          ascending: this.ascending,
          queryKey: this.queryKey.key,
          queryValue: this.queryValue
        }
      })
    },

    async viewItem (item) {
      router.push({ name: 'UserDetails', params: { id: item.id } })
    },
    showCreateUserView () {
      router.push({
        name: 'UserCreate'
      })
    },

    async triggerAssignRoleAction (data) {
      this.selectedUserId = data.item.id
      await store.dispatch('management/userDetails/get', this.selectedUserId)
      this.isAssignRoleModalEnabled = true
    },
    async triggerChangePasswordAction (data) {
      this.selectedUserId = data.item.id
      await store.dispatch('management/userDetails/get', this.selectedUserId)
      this.isChangePasswordModalEnabled = true
    },
    async triggerLockUserAction (data) {
      this.selectedUserId = data.item.id
      await store.dispatch('management/userDetails/get', this.selectedUserId)
      this.isLockUserModalEnabled = true
    },

    // Select to add new item in assigning roles. If the item added is from removedRoles, add
    // that item instead. The flag originalAssigned will prevent the item to be added in submission.
    select (selectedItem) {
      const itemInRemovedRoles = this.removedRoles.find((item) => item.id === selectedItem.id)
      if (itemInRemovedRoles !== undefined) {
        if (itemInRemovedRoles.originalAssigned) {
          this.removedRoles = this.removedRoles.filter(item => item.id !== selectedItem.id)
          this.selectedRoles.push(itemInRemovedRoles)
        }
      } else {
        this.selectedRoles.push(selectedItem)
      }
    },
    // Remove item in assigning roles. If the item is assigned originally it should
    // push to removedRoles for role unassignment usage in submission.
    remove (removedItem) {
      const itemInSelectedRoles = this.selectedRoles.find((item) => item.id === removedItem.id)
      if (itemInSelectedRoles !== undefined) {
        if (itemInSelectedRoles.originalAssigned) {
          this.removedRoles.push(removedItem)
        }
      }
      this.selectedRoles = this.selectedRoles.filter(item => item.id !== removedItem.id)
    },

    async modalSubmit () {
      if (this.isAssignRoleModalEnabled) {
        await this.assignRoles()
      } else if (this.isLockUserModalEnabled) {
        if (this.user.locked) {
          await this.unlock()
        } else {
          await this.lock()
        }
      } else if (this.isChangePasswordModalEnabled) {
        await this.changePassword()
      }

      if (this.actionError === undefined) {
        this.$nextTick(() => {
          this.isAssignRoleModalEnabled = false
          this.isChangePasswordModalEnabled = false
          this.isLockUserModalEnabled = false

          // Update user list when the action is completed. Mainly for updating the string for lock/unlock user
          store.dispatch('management/usersList/list', {
            pageToken: this.pageToken,
            ascending: this.ascending,
            sortKey: this.sortKey.key,
            queryKey: this.queryKey.key,
            queryValue: this.queryValue
          })
        })
      }
    },
    async assignRoles () {
      await this.selectedRoles.filter(item => item.originalAssigned === undefined).forEach(item => {
        store.dispatch('management/userDetails/assign', item.id)
      })
      await this.removedRoles.forEach(item => {
        store.dispatch('management/userDetails/unassign', item.id)
      })
      // Reset the origianlAssigned state for the assignment after submission as the role list will
      // only refresh in mounted status
      this.roles.filter(item => item.originalAssigned).forEach(item => { item.originalAssigned = undefined })
    },
    // For current design, support infinity lock with no description
    async lock () {
      await store.dispatch('management/userDetails/lock', {
        isLocked: true,
        lockInDays: Infinity,
        description: ''
      })
    },
    async unlock () {
      await store.dispatch('management/userDetails/lock', {
        isLocked: false,
        lockInDays: Infinity,
        description: ''
      })
    },
    async changePassword () {
      await store.dispatch('management/userDetails/changePassword', {
        newPassword: this.newPassword,
        confirmNewPassword: this.confirmNewPassword
      })
    },

    changeQuery (data) {
      this.queryKey = data
    },

    submitQuery (evt) {
      evt.preventDefault()
      router.push({
        name: 'UserList',
        query: {
          pageToken: this.pageToken,
          sort: this.sortKey.key,
          ascending: this.ascending,
          queryKey: this.queryKey.key,
          queryValue: this.queryValue
        }
      })
    },

    changeSort (data) {
      this.sortKey = data
    },

    changeOrder () {
      this.ascending = !this.ascending
    }
  },

  beforeRouteUpdate (to, from, next) {
    store.dispatch('management/usersList/list', {
      pageToken: to.query.pageToken,
      ascending: this.ascending,
      sortKey: this.sortKey.key,
      queryKey: this.queryKey.key,
      queryValue: this.queryValue
    })
    next()
  }
}
</script>
